import type { ApiEndpointKeyType } from './api-endpoint-key';
import type { BdBackendHost } from './hosted-api-endpoint';
import { META_BACKEND_API_URL_KEY } from './hosted-api-endpoint';

/**
 * Sentinel host used across the deployment configs (k8s env / config.json) to
 * mark an endpoint that is intentionally NOT available for a given country or
 * environment. The config key still has to exist (some services resolve their
 * `BASE` from it), but pointing it at this host keeps the feature disabled.
 *
 * Endpoints resolving to this host must be excluded from the diagnostics page —
 * otherwise every such placeholder shows up as a permanent connectivity error
 * for an endpoint the country legitimately does not have.
 */
export const UNAVAILABLE_ENDPOINT_HOST = 'not-a-real-api.de';

export interface DiagnosticsEndpoint {
  /**
   * First endpoint key that mapped to this origin — or the host env var name
   * (`META_BACKEND_API_URL`, …) when the origin is a Modulith host, since
   * naming one process after whichever folded module happened to come first
   * would mislead support.
   */
  name: string;
  /** All endpoint keys sharing the same origin */
  endpointKeys: ApiEndpointKeyType[];
  /** scheme + host (e.g. https://api.example.com) */
  origin: string;
  /** Full health check URL: origin + docPath */
  healthCheckUrl: string;
  /** Documentation path segment */
  docPath: string;
  /** Optional human-readable description (e.g. "Externer SSO Dienstleister") */
  description?: string;
}

/**
 * Resolves endpoint keys against the URL map and doc-path map,
 * deduplicating by `origin` (host).
 *
 * The diagnostics page shows and health-checks one row per host: the network
 * test only probes reachability (a `no-cors`, opaque fetch — the HTTP status /
 * path is irrelevant), so several endpoint keys that share the same host must
 * collapse into a single entry. Otherwise a host reachable under two different
 * doc paths (e.g. CH's `switzerland-clinics-api` via both `/api/` and
 * `/api/khbenchgateway/`) would appear as a confusing duplicate row.
 *
 * @param endpointKeys All endpoint keys to resolve (protected + unprotected).
 * @param resolvedUrls Resolved URLs from BdAppConfigResponse.apiEndpoints.
 * @param docPaths Mapping of endpoint keys to their doc/health-check paths.
 * @param backendHosts Origin per Modulith host (`BdAppConfigResponse.backendHosts`, BDA-6595).
 *   Keys resolved to that origin collapse — like every other shared origin — into one row,
 *   which is then named after the host env var instead of the first key that happened to
 *   arrive. A key still on a legacy subdomain keeps the key as its name.
 * @returns Deduplicated list of DiagnosticsEndpoint entries, one per host.
 */
export function resolveDiagnosticsEndpoints(
  endpointKeys: ApiEndpointKeyType[],
  resolvedUrls: Partial<Record<ApiEndpointKeyType, string>>,
  docPaths: Partial<Record<ApiEndpointKeyType, string>>,
  backendHosts: Readonly<Partial<Record<BdBackendHost, string>>> = {},
): DiagnosticsEndpoint[] {
  const groupMap = new Map<string, DiagnosticsEndpoint>();

  for (const key of endpointKeys) {
    const docPath = docPaths[key];
    if (docPath === undefined) {
      continue;
    }

    const rawUrl = resolvedUrls[key];
    if (!rawUrl) {
      continue;
    }

    let url: URL;
    try {
      url = new URL(rawUrl);
    } catch {
      continue;
    }

    // Skip endpoints that are not available for this country/environment.
    if (url.hostname === UNAVAILABLE_ENDPOINT_HOST) {
      continue;
    }

    const origin = url.origin;
    const host = hostedBy(origin, backendHosts);
    const existing = groupMap.get(origin);

    if (existing) {
      existing.endpointKeys.push(key);
      if (host) {
        nameAfterHost(existing, host);
      }
    } else {
      const endpoint: DiagnosticsEndpoint = {
        name: key,
        endpointKeys: [key],
        origin,
        healthCheckUrl: `${origin}${docPath}`,
        docPath,
      };
      if (host) {
        nameAfterHost(endpoint, host);
      }
      groupMap.set(origin, endpoint);
    }
  }

  return Array.from(groupMap.values());
}

/**
 * The Modulith host whose origin the resolved URL is on. The host has one URL space under
 * `/api`, the same shape a legacy subdomain has, so the origin is the only thing that tells
 * them apart (BDA-6595).
 */
function hostedBy(
  origin: string,
  backendHosts: Readonly<Partial<Record<BdBackendHost, string>>>,
): BdBackendHost | null {
  const match = (Object.entries(backendHosts) as [BdBackendHost, string][]).find(
    ([, hostOrigin]) => hostOrigin === origin,
  );
  return match ? match[0] : null;
}

function nameAfterHost(endpoint: DiagnosticsEndpoint, host: BdBackendHost): void {
  endpoint.name = META_BACKEND_API_URL_KEY[host];
  endpoint.description = `Spring Modulith host (${host})`;
}
